import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Paper, Button, CircularProgress } from '@material-ui/core';
import { FaDownload } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import axios from 'axios';
import { SERVER_NAME, futLogoIcon200 } from './config';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    maxWidth: '100%',
    margin: '0',
    padding: 0,
    backgroundColor: '#000000',
    overflowX: 'hidden',
    height: 'calc(100vh - 60px)', // Subtract header height
    display: 'flex',
    flexDirection: 'column',
  },
  contentWrapper: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: theme.spacing(2),
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    // Remove excess padding for mobile
    '@media (max-width: 600px)': {
      padding: '0px',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    paddingBottom: theme.spacing(1),
    // Adjust spacing for mobile
    '@media (max-width: 600px)': {
      padding: '0 6px',
      marginBottom: theme.spacing(1),
      position: 'relative', // Needed for absolute positioning context
      minHeight: '60px',    // Ensure space for the button
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    // Adjust for mobile
    '@media (max-width: 600px)': {
      marginBottom: '8px',
      width: '100%',
    },
  },
  logo: {
    height: '30px',
    marginRight: theme.spacing(2),
  },
  title: {
    fontWeight: 'bold',
    color: '#ffffff',
    fontSize: '28px',
    // Smaller text on mobile
    '@media (max-width: 600px)': {
      fontSize: '22px',
    },
  },
  
  downloadButton: {
    backgroundColor: '#ff6600',
    color: '#ffffff',
    padding: '6px 12px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: '#e65c00',
    },

    // Mobile adjustments
    '@media (max-width: 600px)': {
      alignSelf: 'flex-start', // Move to the left instead of right
      padding: '6px',
      minWidth: 'unset',
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      marginBottom: '4px',
      marginTop: '8px',
      justifyContent: 'center',
      position: 'absolute',
      right: '16px',       // Increase right margin to ensure visibility
      top: '12px'          // Move down a bit for better spacing
    },
  },
  downloadIcon: {
    fontSize: '1.2em',
    '@media (max-width: 600px)': {
      fontSize: '1em',
      margin: 0,
    },
  },
  downloadText: {
    marginLeft: theme.spacing(1),
    '@media (max-width: 600px)': {
      display: 'none',
    },
  },
  content: {
    backgroundColor: '#ffffff',
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    flex: 1,
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 140px)', // Allow scrolling within the content
    // Balanced minimal padding on mobile
    '@media (max-width: 600px)': {
      padding: '8px 6px',
      borderRadius: 0,
    },
    '& a': {
      color: '#0056b3',
      textDecoration: 'none',
    },
    '& a:hover': {
      textDecoration: 'underline',
    },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      display: 'block',
      margin: '10px auto',
    },
    '& svg': {
      maxWidth: '100%',
      width: '100%',
      height: 'auto',
      maxHeight: '500px', // Limit maximum height
      display: 'block',
      margin: '10px auto',
    },
    // Add specific SVG container styles to enforce aspect ratio
    '& .svg-container': {
      position: 'relative',
      width: '100%',
      height: '0',
      paddingBottom: '56.25%', // 16:9 aspect ratio
      overflow: 'hidden',
      margin: '15px auto',
    },
    '& .svg-content': {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
    },
    // Responsive adjustments for mobile
    '@media (max-width: 600px)': {
      '& svg': {
        maxHeight: '250px',
      },
      '& .chart-container': {
        maxHeight: '250px',
        overflow: 'hidden',
      }
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      margin: '15px 0',
    },
    '& td, & th': {
      border: '1px solid #ddd',
      padding: '8px',
      textAlign: 'left',
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  error: {
    color: 'red',
    padding: theme.spacing(2),
  },
  debug: {
    fontSize: '12px',
    color: '#999',
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    maxHeight: '200px',
    overflowY: 'auto'
  }
}));

const NewsletterContent = ({ refreshKey = 0, ...props }) => {
  // Router hook for navigation
  const navigate = useNavigate();
  const { date } = useParams(); // Extract date from URL path
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [specialPageContent, setSpecialPageContent] = useState(null);
  const [isSpecialPage, setIsSpecialPage] = useState(false);
  const [newsletterHtml, setNewsletterHtml] = useState('');
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [newsDate, setNewsDate] = useState(date || null); // Use URL date or null
  const [newsletterDateRef, setNewsletterDateRef] = useState('');
  const [debugInfo, setDebugInfo] = useState({ attempts: [], currentStep: '', loadedFrom: '' });
  const contentRef = useRef(null);
  const imagesRef = useRef({ total: 0, loaded: 0 });
  const firstTimeRef = useRef(true);
  const hasRenderedContentRef = useRef(false);
  const hasInitializedRef = useRef(false);
  
  // Request limiter for network requests to prevent infinite loops
  const requestLimiterRef = useRef({
    lastRequestTime: 0,
    requestCount: 0,
    newsRequestCount: 0
  });
  
  // Define URL patterns that should be blocked to prevent infinite loops
  // Note: We're being more selective to allow legitimate first requests but block problematic patterns
  const blockedUrlPatterns = [
    '/newsletter/t',
    '/news/letter',
    '/newsletter/token'
    // Removed 'e3d.ai/apitest/getNewsletter' and 'e3d.ai/apitest' to allow legitimate requests
    // We'll rely on the request limiter to prevent infinite loops instead
  ];
  
  // Circuit breaker for network requests to prevent infinite loops - ENHANCED VERSION
  useEffect(() => {
    console.log('Setting up enhanced network request circuit breaker');
    
    // Create a cache for responses
    const responseCache = new Map();
    
    console.log('Enhanced URL blocking active with patterns:', blockedUrlPatterns);
    
    // Save the original fetch and XMLHttpRequest functions
    const originalFetch = window.fetch;
    const originalXHR = window.XMLHttpRequest.prototype.open;
    
    // Override fetch to implement request blocking and caching
    window.fetch = function(...args) {
      const url = args[0];
      const urlStr = typeof url === 'string' ? url : url?.url || '';
      const now = Date.now();
      
      // Check if URL matches any blocked pattern
      if (urlStr && blockedUrlPatterns.some(pattern => urlStr.includes(pattern))) {
        console.warn(`BLOCKED: Request to blocked URL pattern: ${urlStr}`);
        return Promise.resolve(new Response('Blocked URL', { status: 403 }));
      }
      
      // Check for newsletter related URLs
      if (urlStr && (urlStr.includes('/news') || urlStr.includes('letter'))) {
        requestLimiterRef.current.newsRequestCount++;
        requestLimiterRef.current.requestCount++;
        
        // SUPER AGGRESSIVE circuit breaker: 
        // - If more than 3 requests in 30 seconds
        // - Or if more than 10 total requests in this session
        const timeSinceLastRequest = now - requestLimiterRef.current.lastRequestTime;
        
        if ((requestLimiterRef.current.newsRequestCount > 3 && timeSinceLastRequest < 30000) || 
            requestLimiterRef.current.newsRequestCount > 10) {
          console.warn(`CIRCUIT BREAKER TRIPPED: Blocked excessive fetch request to ${urlStr}. Count: ${requestLimiterRef.current.newsRequestCount}, Time since last: ${timeSinceLastRequest}ms`);
          
          // Try to return from cache if available
          if (responseCache.has(urlStr)) {
            console.log(`Returning cached response for ${urlStr}`);
            const cachedResponse = responseCache.get(urlStr);
            return Promise.resolve(cachedResponse.clone());
          }
          
          return Promise.resolve(new Response('Rate limited', { status: 429, statusText: 'Too Many Requests' }));
        }
        
        requestLimiterRef.current.lastRequestTime = now;
        
        // If URL is already in cache, return cached response
        if (responseCache.has(urlStr)) {
          console.log(`Using cached response for ${urlStr}`);
          return Promise.resolve(responseCache.get(urlStr).clone());
        }
      }
      
      // For newsletter content requests, we'll cache the response
      if (urlStr && urlStr.includes('/news')) {
        return originalFetch.apply(this, args)
          .then(response => {
            // Only cache successful responses
            if (response.ok) {
              // Clone the response before caching
              responseCache.set(urlStr, response.clone());
              console.log(`Cached response for ${urlStr}`);
            }
            return response;
          });
      }
      
      // Allow other requests
      return originalFetch.apply(this, args);
    };
    
    // Override XMLHttpRequest to implement request limiting
    window.XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      const urlStr = url || '';
      const now = Date.now();
      
      // Check if URL matches any blocked pattern
      if (urlStr && blockedUrlPatterns.some(pattern => urlStr.includes(pattern))) {
        console.warn(`BLOCKED: XMLHttpRequest to blocked URL pattern: ${urlStr}`);
        throw new Error('Request to blocked URL');
      }
      
      // Check for newsletter related URLs
      if (urlStr && (urlStr.includes('/news') || urlStr.includes('letter'))) {
        requestLimiterRef.current.newsRequestCount++;
        requestLimiterRef.current.requestCount++;
        
        // SUPER AGGRESSIVE circuit breaker
        const timeSinceLastRequest = now - requestLimiterRef.current.lastRequestTime;
        
        if ((requestLimiterRef.current.newsRequestCount > 3 && timeSinceLastRequest < 30000) || 
            requestLimiterRef.current.newsRequestCount > 10) {
          console.error(`CIRCUIT BREAKER TRIPPED: Blocked excessive XHR request to ${urlStr}. Count: ${requestLimiterRef.current.newsRequestCount}, Time since last: ${timeSinceLastRequest}ms`);
          throw new Error('Too many requests blocked by circuit breaker');
        }
        
        requestLimiterRef.current.lastRequestTime = now;
      }
      
      // Allow the request
      return originalXHR.apply(this, [method, url, ...rest]);
    };
    
    // Cleanup function to restore original fetch and XHR
    return () => {
      window.fetch = originalFetch;
      window.XMLHttpRequest.prototype.open = originalXHR;
      console.log('Restored original fetch and XHR functions on unmount');
    };
  }, []);

  // Function to load special pages like token or NFT pages
  const loadSpecialPage = useCallback((path) => {
    console.log(`Loading special page: ${path}`);
    setIsSpecialPage(true);
    
    // Create the URL for the special page
    const apiUrl = `${SERVER_NAME}/getNewsletter?specialPage=${path}`;
    
    // Fetch the special page content
    axios.get(apiUrl)
      .then(response => {
        console.log('Special page loaded successfully');
        setSpecialPageContent(response.data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading special page:', err);
        setError(`Failed to load special page: ${err.message}`);
        setLoading(false);
      });
  }, []);

  // Process the newsletter HTML to correctly render embedded content
  const processNewsletterHtml = useCallback((html, dateString = '') => {
    if (!html) return '';
    console.log('Processing newsletter HTML...');
    
    // Create a temporary DOM element to parse and manipulate the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Use the provided date string from the API response
    let newsletterDateString = dateString;
    console.log('Using newsletter date for link processing:', newsletterDateString);
    
    // Fix relative URLs in the newsletter content
    const baseUrl = window.location.origin;
    // Use both origin-based path and PUBLIC_URL for more robust resource loading
    const newsletterBase = `${baseUrl}/newsletter`;
    const publicUrlBase = `${process.env.PUBLIC_URL}/newsletter`;
    console.log('Newsletter base URLs:', newsletterBase, publicUrlBase);
    
    // Process links
    const links = tempDiv.querySelectorAll('a');
    console.log(`Processing ${links.length} links`);
    links.forEach(link => {
      const href = link.getAttribute('href');
      const linkText = link.textContent.trim();
      
      console.log(`Processing link: ${linkText}, href=${href}`);
      
      // Fix 'Back to Newsletter' links to point to the /news route
      if (linkText.includes('Back to Newsletter')) {
        console.log('Found Back to Newsletter button, setting href to /news');
        link.setAttribute('data-original-href', href); // Store original for debugging
        link.setAttribute('href', '/news');
        return;
      }
      
      // Handle 'View All Tokens' and 'View All NFTs' buttons
      if (linkText.includes('View All') && newsletterDateString) {
        // Store the original type (tokens or NFTs) for later use
        const isTokens = linkText.includes('Tokens');
        const itemType = isTokens ? 'tokens' : 'nfts';
        
        console.log(`Found View All ${itemType} button, adding special handling with date:`, newsletterDateString);
        
        // COMPLETELY REPLACE the link with a custom button element to avoid any browser issues
        const buttonText = linkText;
        const parentNode = link.parentNode;
        
        // Create a button that looks like a link but is actually a button
        const newButton = document.createElement('button');
        newButton.innerHTML = buttonText;
        newButton.className = 'custom-view-all-button view-all-link';
        console.log('Created custom button with classes:', newButton.className);
        newButton.style.cssText = 'background: none; border: none; color: blue; text-decoration: underline; cursor: pointer; font-size: inherit; font-family: inherit; padding: 0; margin: 0; font-weight: inherit;';
        
        // Add data attributes for debugging and tracking
        newButton.setAttribute('data-original-href', href);
        newButton.setAttribute('data-item-type', itemType);
        newButton.setAttribute('data-newsletter-date', newsletterDateString);
        
        // No click handler here - we'll use the global click handler instead
        // Just add a data attribute to identify this as our custom button that should trigger the loading
        newButton.setAttribute('data-handle-in-global-handler', 'true');
        
        // Replace the original link with our custom button
        if (parentNode) {
          parentNode.replaceChild(newButton, link);
          console.log('Replaced View All link with custom button');
        }
        
        return;
      }
      
      // Also check for links to newsletter HTML files - both relative and absolute URLs
      if (href && (
          // Check for newsletter filenames
          (href.includes('newsletter_') && href.endsWith('.html')) ||
          // Check for full URLs to newsletter files
          href.includes('/newsletter/newsletter_') ||
          // Check for host-relative URLs
          (href.startsWith('/') && href.includes('/newsletter/'))
      )) {
        console.log('Found link to newsletter HTML file, setting href to /news');
        link.setAttribute('data-original-href', href); // Store original for debugging
        link.setAttribute('href', '/news');
        return;
      }
      
      if (href && (href.startsWith('./') || (!href.startsWith('http') && !href.startsWith('#') && !href.startsWith('/')))) {
        const relativePath = href.startsWith('./') ? href.substring(2) : href;
        // Check if the path already contains 'newsletter' to avoid duplication
        if (relativePath.startsWith('newsletter/')) {
          // Path already includes 'newsletter/', so just prepend the base URL
          link.setAttribute('data-original-href', href); // Store original for debugging
          link.setAttribute('href', `${baseUrl}/${relativePath}`);
        } else {
          // Regular case - use publicUrlBase
          link.setAttribute('data-original-href', href); // Store original for debugging
          link.setAttribute('href', `${publicUrlBase}/${relativePath}`);
        }
      }
    });
    
    // Process images
    const images = tempDiv.querySelectorAll('img');
    console.log(`Processing ${images.length} images`);
    images.forEach(img => {
      const src = img.getAttribute('src');
      if (src && (src.startsWith('./') || (!src.startsWith('http') && !src.startsWith('/')))) {
        const relativePath = src.startsWith('./') ? src.substring(2) : src;
        
        // Check if the path already contains 'newsletter' to avoid duplication
        if (relativePath.startsWith('newsletter/')) {
          // Path already includes 'newsletter/', so just prepend the base URL
          img.setAttribute('data-original-src', src); // Store original for debugging
          img.setAttribute('src', `${baseUrl}/${relativePath}`);
          
          // Add fallback logic in case base URL doesn't work
          img.setAttribute('onerror', `this.dataset.error = true; this.src = '${publicUrlBase}/${relativePath.substring(10)}'`);
        } else {
          // Regular case - use publicUrlBase
          img.setAttribute('data-original-src', src); // Store original for debugging
          img.setAttribute('src', `${publicUrlBase}/${relativePath}`);
          
          // Add fallback logic in case public URL doesn't work
          img.setAttribute('onerror', `this.dataset.error = true; if(this.src.includes('${publicUrlBase}')) { this.src = '${newsletterBase}/' + this.dataset.originalSrc.replace('./',''); }`);
        }
      }
      
      // Add load event listener to track when image loads
      img.setAttribute('onload', 'this.dataset.loaded = true');
      
      // Fix image styling
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      img.style.margin = '10px auto';
    });
    
    // Process stylesheets
    const styleLinks = tempDiv.querySelectorAll('link[rel="stylesheet"]');
    console.log(`Processing ${styleLinks.length} stylesheets`);
    styleLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (href && (href.startsWith('./') || (!href.startsWith('http') && !href.startsWith('/')))) {
        const relativePath = href.startsWith('./') ? href.substring(2) : href;
        // Use public URL path which works in both development and production
        link.setAttribute('data-original-href', href); // Store original for debugging
        link.setAttribute('href', `${publicUrlBase}/${relativePath}`);
      }
    });
    
    // Ensure SVGs have proper dimensions and responsiveness
    const svgs = tempDiv.querySelectorAll('svg');
    console.log(`Processing ${svgs.length} SVGs for responsive display`);
    svgs.forEach(svg => {
      // Determine if this is a chart by checking for chart classes or elements
      const isChart = svg.classList.contains('chart') || 
                     svg.querySelector('rect.background') || 
                     svg.querySelectorAll('path').length > 5 ||
                     svg.querySelectorAll('rect').length > 10;
      
      // Create a container wrapper element for the SVG
      if (isChart) {
        const wrapper = document.createElement('div');
        wrapper.className = 'chart-container';
        wrapper.style.cssText = 'width: 100%; max-height: 300px; overflow: hidden; margin: 10px auto;';
        
        // Set fixed dimensions to prevent infinite growth
        svg.setAttribute('width', '100%');
        svg.setAttribute('height', '250');
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        
        // Ensure SVG has a viewBox for proper scaling
        if (!svg.hasAttribute('viewBox')) {
          svg.setAttribute('viewBox', '0 0 600 300');
        }
        
        // Wrap the SVG in the new container (done during rendering in React)
        svg.setAttribute('data-wrap-in-container', 'true');
        svg.setAttribute('data-container-type', 'chart');
      } else {
        // For non-chart SVGs, make them responsive but with controlled height
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        
        // Ensure SVG has a viewBox
        if (!svg.hasAttribute('viewBox')) {
          // If no viewBox, create one based on width/height or set a default
          const width = svg.getAttribute('width') || svg.width || 600;
          const height = svg.getAttribute('height') || svg.height || 300;
          svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
        }
        
        // Set width to be responsive (100% of container)
        svg.setAttribute('width', '100%');
        
        // Set height to auto but with max height
        svg.setAttribute('height', 'auto');
        svg.style.maxHeight = '300px';
      }
      
      // Add a CSS class for additional styling via stylesheet
      svg.classList.add('responsive-svg');
    });
    
    // Format tables to be responsive
    const tables = tempDiv.querySelectorAll('table');
    console.log(`Processing ${tables.length} tables`);
    tables.forEach(table => {
      table.style.width = '100%';
      table.style.borderCollapse = 'collapse';
      table.style.margin = '15px 0';
    });
    
    return tempDiv.innerHTML;
  }, []);

  // Handle images loaded state
  const handleImagesLoaded = useCallback(() => {
    try {
      // Make sure we have a valid content reference
      if (!contentRef.current) {
        console.warn('Cannot handle images: contentRef.current is null');
        return;
      }
      
      // Prevent repeated calls by checking a flag
      if (window.__handlingImages) {
        console.log('Already handling images, skipping');
        return;
      }
      window.__handlingImages = true;
      
      // Safely query for images
      let images;
      try {
        images = contentRef.current.querySelectorAll('img');
        if (!images) {
          console.warn('querySelectorAll for images returned null/undefined');
          setImagesLoaded(true); // Consider it loaded if we can't find images
          window.__handlingImages = false;
          return;
        }
      } catch (err) {
        console.error('Error querying for images:', err);
        setImagesLoaded(true); // Consider it loaded if we can't query
        window.__handlingImages = false;
        return;
      }
      
      // Track image loading status
      imagesRef.current.total = images.length;
      imagesRef.current.loaded = 0;
      
      // If no images, consider it loaded
      if (images.length === 0) {
        setImagesLoaded(true);
        window.__handlingImages = false;
        return;
      }
      
      // Only set to false if we're truly starting a new image loading process
      if (!window.__imagesBeingTracked) {
        setImagesLoaded(false);
        window.__imagesBeingTracked = true;
      }
      
      const handleImageLoad = () => {
        if (imagesRef.current) {
          imagesRef.current.loaded++;
          if (imagesRef.current.loaded >= imagesRef.current.total) {
            setImagesLoaded(true);
            window.__handlingImages = false;
            window.__imagesBeingTracked = false;
          }
        }
      };
      
      const handleImageError = () => {
        // Count failed loads as 'loaded' to avoid blocking forever
        handleImageLoad();
      };
      
      // Safely add event listeners to each image
      Array.from(images).forEach(img => {
        try {
          if (img.complete) {
            handleImageLoad();
          } else {
            img.addEventListener('load', handleImageLoad);
            img.addEventListener('error', handleImageError);
          }
        } catch (err) {
          console.warn('Error processing individual image:', err);
          handleImageError(); // Count as error to prevent blocking
        }
      });
    } catch (err) {
      console.error('Unhandled error in handleImagesLoaded:', err);
      setImagesLoaded(true); // Consider it loaded if we encounter errors
    }
  }, []);

  // Handle PDF download with page-aware rendering
  const handleDownloadPDF = useCallback(async () => {
    const container = document.querySelector('.newsletter-container');
    
    if (!container) {
        console.error("Error: The container element does not exist.");
        return;
    }

    html2canvas(container, { scale: 2 }).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 210; // A4 width in mm (210mm)
        const pageHeight = 297; // A4 height in mm (297mm)
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const pdf = new jsPDF('p', 'mm', 'a4');

        let y = 0; // Current position in PDF

        while (y < imgHeight) {
            pdf.addImage(imgData, 'PNG', 0, -y, imgWidth, imgHeight);
            y += pageHeight; // Move to next page
            if (y < imgHeight) pdf.addPage(); // Add new page if needed
        }

        pdf.save('E3D_Newsletter.pdf');
    }).catch((error) => {
        console.error("Error generating PDF:", error);
    });
  }, [contentRef, loading, error, setPdfGenerating]);
  
  // Fallback function to create a text-only PDF if image processing fails
  const createTextOnlyPDF = (pdf, text) => {
    try {
      pdf.setFontSize(12);
      pdf.text('E3D Newsletter (Text Only Version)', 20, 20);
      pdf.setFontSize(10);
      
      // Split text into chunks to avoid browser limitations
      const maxLength = 5000;
      const chunks = [];
      
      for (let i = 0; i < text.length; i += maxLength) {
        chunks.push(text.substring(i, i + maxLength));
      }
      
      // Process text in manageable chunks
      let y = 30;
      chunks.forEach(chunk => {
        const lines = pdf.splitTextToSize(chunk, 170);
        
        lines.forEach(line => {
          if (y > 280) {
            pdf.addPage();
            y = 20;
          }
          
          // Add the line and increment position
          pdf.text(line, 20, y);
          y += 5;
        });
      });
      
      pdf.save('E3D_Newsletter_Text.pdf');
    } catch (textErr) {
      console.error('Error creating text-only PDF:', textErr);
      alert('Unable to create PDF. Please try again later.');
    }
  };



// Request cache to prevent duplicate fetches
const requestCacheRef = useRef({});

// Common function to process the newsletter HTML response - used by both fresh fetch and cache
const processNewsletterResponse = useCallback((html, extractedDate) => {
  // Store the extracted date in component state for later use
  setNewsletterDateRef(extractedDate);

  console.log('FINAL newsletter date for tokens/NFTs links:', extractedDate);
  console.log('Processing newsletter HTML...', html.substring(0, 200) + '...');

  setDebugInfo(prev => ({ ...prev, currentStep: 'processing HTML', loadedFrom: 'getNewsletter API', newsletterDate: extractedDate }));

  // Process the newsletter HTML - ensure all paths are absolute
  const processedHtml = processNewsletterHtml(html, extractedDate);
  console.log('Setting processed HTML to state');

  // Inject responsive CSS fixes for mobile
  const mobileStyleFixes = `
    <style type="text/css">
      @media (max-width: 600px) {
        body, html {
          margin: 0 !important;
          padding: 0 !important;
          overflow-x: hidden !important;
        }
        .newsletter-container, .newsletter-content, .content-wrapper {
          padding-left: 6px !important;
          padding-right: 6px !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          width: calc(100% - 12px) !important;
          box-sizing: border-box !important;
        }
        h1, h2, h3 {
          margin-left: 0 !important;
          margin-right: 0 !important;
          width: 100% !important;
          font-size: 1.2em !important;
        }
        .tables-row {
          padding: 0 !important;
          gap: 10px !important;
        }
        .styled-table {
          margin-left: 0 !important;
          margin-right: 0 !important;
          width: 100% !important;
          font-size: 0.9em !important;
        }
        /* Ensure text doesn't get cut off */
        .title-container, .header-section {
          width: 100% !important;
          padding: 6px !important;
          box-sizing: border-box !important;
        }
        /* Ensure charts display correctly */
        svg {
          width: 100% !important;
          height: auto !important;
          max-height: 300px !important;
          display: block !important;
        }
      }
    </style>
  `;
  
  setNewsletterHtml(mobileStyleFixes + processedHtml);
  imagesRef.current = { total: 0, loaded: 0 };
  setImagesLoaded(false);
  setDebugInfo(prev => ({ ...prev, currentStep: 'HTML processed', loadedFrom: 'getNewsletter API' }));
  
  setLoading(false);
  console.log('Newsletter loading complete');
  }, [processNewsletterHtml]);
  
  // Define fetchNewsletter outside useEffect for proper dependency handling
  const fetchNewsletter = useCallback(async () => {
    try {
      // Check if we've already fetched this newsletter recently
      const baseURL = `${SERVER_NAME}/getNewsletter`;
      const dateToUse = newsDate || date; // Use state newsDate or URL date parameter
      const url = dateToUse ? `${baseURL}?newsDate=${dateToUse}` : baseURL;
      
      if (dateToUse) {
        console.log(`Fetching newsletter with date: ${dateToUse}`);
      }
      const cacheKey = url;
      
      // Only block URLs that both match our patterns AND we've seen a recent request to
      // This allows the first legitimate request to go through
      const blockedPatternFound = blockedUrlPatterns.some(pattern => url.includes(pattern));
      const isRepeatedRequest = requestCacheRef.current._requestTimes && 
                             requestCacheRef.current._requestTimes[url] && 
                             (Date.now() - requestCacheRef.current._requestTimes[url].latest < 5000);
                             
      if (blockedPatternFound && isRepeatedRequest) {
        console.warn(`⚠️ Preventing fetch to potentially problematic URL: ${url} (repeated request detected)`);
        setError('Request blocked by safety circuit breaker after duplicate requests. Try refreshing the page.');
        setLoading(false);
        return;
      }
      
      // Track this request time
      if (!requestCacheRef.current._requestTimes) {
        requestCacheRef.current._requestTimes = {};
      }
      
      // Record both the first and latest request time
      if (!requestCacheRef.current._requestTimes[url]) {
        requestCacheRef.current._requestTimes[url] = { 
          first: Date.now(),
          latest: Date.now(),
          count: 1
        };
      } else {
        requestCacheRef.current._requestTimes[url].latest = Date.now();
        requestCacheRef.current._requestTimes[url].count += 1;
      }
      
      // Check if we have a cached response for this URL in the last 5 minutes
      const now = Date.now();
      const cachedResponse = requestCacheRef.current[cacheKey];
      if (cachedResponse && (now - cachedResponse.timestamp) < 5 * 60 * 1000) {
        console.log('🔄 Using cached newsletter response instead of fetching again');
        processNewsletterResponse(cachedResponse.data, cachedResponse.extractedDate);
        return;
      }
      
      setLoading(true);
      setError(null);
      setDebugInfo({ attempts: [], currentStep: 'starting', loadedFrom: '' });
      console.log('Fetching newsletter content...');
      console.log('Fetching newsletter from:', url);
      
      const response = await axios.get(url);
      
      if (response.status !== 200) {
        throw new Error(`Failed to fetch the latest newsletter: ${response.statusText}`);
      }
      
      const html = response.data;
      
      if (!html) {
        throw new Error('Newsletter content is empty');
      }
      
      // Extract newsletter date from the HTML content to match with token/NFT files
      let extractedDate = '';
      
      // First, prioritize extracting the date from the actual newsletter HTML file URL references
      // This is the most reliable method to match with the corresponding token/NFT files
      const htmlDateMatch = html.match(/newsletter_([\d-]+_[\d-]+)\.html/);
      if (htmlDateMatch && htmlDateMatch[1]) {
        extractedDate = htmlDateMatch[1];
        console.log('Extracted newsletter date from HTML content reference:', extractedDate);
      }
      // Try to get date from response headers (X-Newsletter-Date)
      else if (response.headers && response.headers['x-newsletter-date']) {
        extractedDate = response.headers['x-newsletter-date'];
        console.log('Extracted newsletter date from headers:', extractedDate);
      } 
      // Check if we have a URL with the date pattern
      else {
        const urlDateMatch = response.config?.url?.match(/newsletter_([\d-]+_[\d-]+)\.html/);
        if (urlDateMatch && urlDateMatch[1]) {
          extractedDate = urlDateMatch[1];
          console.log('Extracted newsletter date from URL:', extractedDate);
        } else {
          console.warn('Could not extract newsletter date from response');
          // If we can't find a date, use current date with time as fallback
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const hours = String(now.getHours()).padStart(2, '0');
          const minutes = String(now.getMinutes()).padStart(2, '0');
          const seconds = String(now.getSeconds()).padStart(2, '0');
          extractedDate = `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
          console.log('Using current date as fallback:', extractedDate);
        }
      }
      
      // Store the successful response in our cache with a timestamp
      requestCacheRef.current[cacheKey] = {
        data: html,
        extractedDate,
        timestamp: Date.now()
      };
      
      // Process the newsletter content
      processNewsletterResponse(html, extractedDate);
    } catch (error) {
      console.error('Error fetching newsletter:', error);
      setError(error.message || 'Error fetching the latest newsletter');
      setDebugInfo(prev => ({ ...prev, currentStep: 'error', error: error.message }));
      // Make sure we set loading to false if there's an error
      setLoading(false);
    }
  }, [processNewsletterHtml, newsDate, blockedUrlPatterns]);
  
  // Store fetchNewsletter in a ref to avoid dependency cycles
  const fetchNewsletterRef = useRef();
  fetchNewsletterRef.current = fetchNewsletter;
  
  // Fetch the latest newsletter content when the component mounts - strictly once only
  useEffect(() => {
    if (hasInitializedRef.current) {
      console.log('Newsletter already initialized, skipping fetch');
      return;
    }
    
    console.log('Performing one-time newsletter initialization');
    // Log if we have a date from URL
    if (date) {
      console.log(`Initializing with date from URL parameter: ${date}`);
      setNewsDate(date);
    }
    
    // Use the ref to avoid dependency cycles
    fetchNewsletterRef.current();
    hasInitializedRef.current = true;
    firstTimeRef.current = false;
  }, []); // No dependencies - truly run once on mount
  
  // Allow manual refresh with refreshKey prop
  useEffect(() => {
    if (refreshKey > 0) { // Skip the initial render
      try {
        console.log('Force refreshing newsletter with new data');
        // Remove reference to undefined setShowDebug
        setLoading(true);
        setError(null);
        
        // If we have a date in the URL but no newsDate state,
        // make sure we set it before fetching
        if (date && !newsDate) {
          setNewsDate(date);
        }
        
        fetchNewsletterRef.current();
      } catch (err) {
        console.error('Error refreshing newsletter:', err);
      }
    }
  }, [refreshKey]); // Only depend on refreshKey, not fetchNewsletter

  // Effect to render newsletter content and execute scripts
  useEffect(() => {
    // Skip rendering regular newsletter content if we're showing a special page
    if (isSpecialPage) {
      console.log('Skipping regular newsletter render because special page is active');
      return;
    }
    
    // Only proceed if we have the newsletter HTML and we're not in a loading or error state
    if (contentRef.current && newsletterHtml && !loading && !error) {
      console.log('Rendering newsletter HTML content...', newsletterHtml.substring(0, 100) + '...');
      
      // Clear previous content first to avoid potential issues
      contentRef.current.innerHTML = '';

      // Set the new content
      contentRef.current.innerHTML = newsletterHtml;
      console.log('Newsletter content set to DOM');
      
      // Extract scripts before they are executed
      const extractedScripts = [];
      const scriptElements = contentRef.current.querySelectorAll('script');
      scriptElements.forEach(script => {
        const scriptData = {
          src: script.src,
          textContent: script.textContent,
          attributes: Array.from(script.attributes).map(attr => ({
            name: attr.name,
            value: attr.value
          }))
        };
        extractedScripts.push(scriptData);
        // Remove the script to prevent automatic execution
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
      
      console.log(`Found ${extractedScripts.length} scripts to execute`);
      
      // Now execute the scripts in sequence after a small delay to ensure DOM is ready
      setTimeout(() => {
        try {
          extractedScripts.forEach((scriptData, index) => {
            let newScript;
            try {
              newScript = document.createElement('script');
              
              // Apply all attributes
              scriptData.attributes.forEach(attr => {
                newScript.setAttribute(attr.name, attr.value);
              });
              
              // For src scripts
              if (scriptData.src) {
                newScript.src = scriptData.src;
                console.log(`Loading external script: ${scriptData.src}`);
              } else if (scriptData.textContent) {
                // Inline script content
                newScript.textContent = scriptData.textContent;
                console.log(`Adding inline script: ${scriptData.textContent.substring(0, 50)}...`);
              }
              
              // Add to DOM to execute (with null check)
              if (contentRef.current) {
                contentRef.current.appendChild(newScript);
                console.log(`Script ${index + 1}/${extractedScripts.length} executed`);
              } else {
                console.warn(`Cannot append script ${index + 1}/${extractedScripts.length} - container is null`);
              }
            } catch (scriptErr) {
              console.error(`Error executing script ${index + 1}/${extractedScripts.length}:`, scriptErr);
              if (newScript) {
                try {
                  document.body.appendChild(newScript);
                } catch (err) {
                  console.error('Could not append to document.body either:', err);
                }
              }
            }
          });
        } catch (mainErr) {
          console.error('Error in script execution block:', mainErr);
        }
        
        // Handle images and check if all loaded
        handleImagesLoaded();
      }, 200);
      
      // Process SVGs once instead of using MutationObserver
      const processSVGs = () => {
        if (!contentRef.current) return;
        
        try {
          // Fix any SVGs that might have been added
          const svgs = contentRef.current.querySelectorAll('svg');
          console.log(`Processing ${svgs.length} SVGs once`);
          
          if (svgs && svgs.length > 0) {
            Array.from(svgs).forEach(svg => {
              try {
                // Determine if this is a chart by checking for chart classes or elements
                const isChart = svg.classList.contains('chart') || 
                              svg.querySelector('rect.background') || 
                              svg.querySelectorAll('path').length > 5 ||
                              svg.querySelectorAll('rect').length > 10;
                
                if (isChart) {
                  // For charts we need strict height controls
                  
                  // Set fixed dimensions to prevent infinite growth
                  svg.setAttribute('width', '100%');
                  // Fixed height for charts is important for mobile
                  svg.setAttribute('height', '250');
                  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
                  
                  // Set a viewBox if not present
                  if (!svg.hasAttribute('viewBox')) {
                    svg.setAttribute('viewBox', '0 0 600 300');
                  }
                  
                  // For SVGs that should be wrapped, handle the wrapping here in the live DOM
                  if (!svg.parentNode.classList.contains('chart-container')) {
                    const wrapper = document.createElement('div');
                    wrapper.className = 'chart-container';
                    wrapper.style.cssText = 'width: 100%; max-height: 300px; overflow: hidden; margin: 10px auto;';
                    
                    // Insert wrapper before svg in the DOM
                    svg.parentNode.insertBefore(wrapper, svg);
                    // Move svg into wrapper
                    wrapper.appendChild(svg);
                  }
                } else {
                  // For non-chart SVGs, make them responsive but with controlled height
                  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
                  
                  // Ensure SVG has a viewBox
                  if (!svg.hasAttribute('viewBox')) {
                    const width = svg.getAttribute('width') || svg.width || 600;
                    const height = svg.getAttribute('height') || svg.height || 300;
                    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
                  }
                  
                  // Width is responsive
                  svg.setAttribute('width', '100%');
                  // Height auto with max-height style
                  svg.setAttribute('height', 'auto');
                  svg.style.maxHeight = '300px';
                }
                
                // Add responsive class either way
                svg.classList.add('responsive-svg');
              } catch (err) {
                console.warn('Error processing individual SVG:', err);
              }
            });
          }
        } catch (err) {
          console.error('Error processing SVGs:', err);
        }
      };
      
      // Process links to handle special pages and external links
      const processLinks = () => {
        if (!contentRef.current) return;
        
        try {
          const links = contentRef.current.querySelectorAll('a');
          console.log(`Processing ${links.length} links once`);
          
          links.forEach(link => {
            const href = link.getAttribute('href');
            if (!href) return;
            
            // Handle external links to open in new tab
            if (href.startsWith('http')) {
              link.setAttribute('target', '_blank');
              link.setAttribute('rel', 'noopener noreferrer');
            }
            
            // Handle special token/NFT links
            if (href.includes('/token/') || href.includes('/nft/') || href.includes('/address/')) {
              link.addEventListener('click', (e) => {
                e.preventDefault();
                const specialPath = href.split('/').slice(-2).join('/');
                console.log(`Special link clicked: ${href}, loading as special page: ${specialPath}`);
                loadSpecialPage(specialPath);
                return false;
              });
            }
          });
        } catch (err) {
          console.error('Error processing links:', err);
        }
      };
      
      // Process images once
      const processImages = () => {
        handleImagesLoaded();
      };
      
      // Run one-time processing functions
      console.log('Running one-time content processors');
      processSVGs();
      processLinks();
      processImages();
      
      // Force a repaint to ensure everything is visible
      setTimeout(() => {
        if (contentRef.current) {
          console.log('Forcing repaint of content');
          contentRef.current.style.display = 'none';
          // Force a reflow
          void contentRef.current.offsetHeight;
          contentRef.current.style.display = 'block';
          // Check for images again after repaint
          handleImagesLoaded();
        }
      }, 300);
      
      // Add global click handler to make sure 'View All' links work correctly
      const handleGlobalClick = (e) => {
        // Add detailed logging for every click to help debug
        console.log('Global click on element:', e.target.tagName, 
                    'classList:', e.target.className,
                    'parent:', e.target.parentNode?.tagName);
        
        try {
          // Check if this is the Return to Newsletter button (which we want to exclude)
          const isReturnButton = 
            (e.target.tagName === 'BUTTON' && e.target.textContent.includes('Return to Newsletter')) ||
            (e.target.tagName !== 'BUTTON' && e.target.closest('button') && 
             e.target.closest('button').textContent.includes('Return to Newsletter'));
          
          // If it's the return button, exit early to let the normal onClick handler work
          if (isReturnButton) {
            console.log('Return button clicked - skipping global handler');
            return;
          }
          
          // Otherwise check if it's a link OR one of our custom view-all buttons
          const isButton = e.target.tagName === 'BUTTON';
          const isCustomButton = isButton && (
              e.target.classList.contains('view-all-link') || 
              e.target.classList.contains('custom-view-all-button')
          );
          const isLink = e.target.tagName === 'A';
          const hasParentLink = !!e.target.closest('a');
          const hasParentButton = 
              !!e.target.closest('button.view-all-link') || 
              !!e.target.closest('button.custom-view-all-button');
          
          console.log('Click details:', {
            isButton, isCustomButton, isLink, hasParentLink, hasParentButton,
            text: e.target.textContent?.trim()
          });
          
          if (isLink || hasParentLink || isButton || hasParentButton) {
            // Get the clicked element (either A or BUTTON)
            const element = 
              e.target.tagName === 'A' ? e.target : 
              e.target.closest('a') ? e.target.closest('a') : 
              e.target.tagName === 'BUTTON' ? e.target : 
              e.target.closest('button.view-all-link') || 
              e.target.closest('button.custom-view-all-button');
            
            // If no valid element was found, exit
            if (!element) return;
            
            const linkText = element.textContent.trim();
            const href = element.getAttribute('href') || element.getAttribute('data-original-href');
            
            console.log(`Clicked element: '${linkText}', href='${href}'`);
            
            // Check if this is a View All Tokens/NFTs link/button
            if (linkText.includes('View All')) {
              console.log('Found a View All link/button!');
              
              // Prevent default action (opening link in new tab)
              e.preventDefault();
              e.stopPropagation();
              
              // Extract the newsletter date and type
              let newsletterDate = '';
              let itemType = '';
              
              // Try to get date and type from data attributes first
              if (element.dataset && element.dataset.newsletterDate) {
                newsletterDate = element.dataset.newsletterDate;
                console.log('Using date from element dataset:', newsletterDate);
              } else if (newsletterDateRef.current) {
                newsletterDate = newsletterDateRef.current;
                console.log('Using date from newsletterDateRef:', newsletterDate);
              } else {
                console.error('Could not determine newsletter date!');
                return;
              }
              
              // Determine item type from button data or link text
              if (element.dataset && element.dataset.itemType) {
                itemType = element.dataset.itemType;
              } else {
                // Infer from link text
                itemType = linkText.includes('Tokens') ? 'tokens' : 'nfts';
              }
              console.log('Item type:', itemType);
              
              // Generate the appropriate filename
              const filenamePart = `all_${itemType}_${newsletterDate}.html`;
              console.log('Generated filename:', filenamePart);
              
              // Set loading indicator
              setLoading(true);
              
              // Use API to fetch the special page content
              console.log(`Fetching ${filenamePart} via API`);
              axios.get(`${SERVER_NAME}/getNewsletter`, { 
                params: { specialPage: filenamePart } 
              })
              .then(response => {
                if (response.data) {
                  console.log('Successfully received special page content');
                  // Instead of trying to directly manipulate the DOM with innerHTML,
                  // store the special page content in React state
                  setSpecialPageContent(response.data);
                  setIsSpecialPage(true);
                } else {
                  console.error('Response data is empty');
                  setError('Error: Received empty response from server');
                }
                setLoading(false);
              })
              .catch(err => {
                console.error('Error fetching special page content:', err);
                setError(`Error loading content: ${err.message}`);
                setLoading(false);
              });
            }
          }
        } catch (err) {
          console.error('Error in global click handler:', err);
        }
      };
      
      // Attach the click handler to the content container
      if (contentRef.current) {
        contentRef.current.addEventListener('click', handleGlobalClick);
        console.log('Added global click handler for newsletter links');
        
        // Add cleanup function to remove event listener when component unmounts
        return () => {
          try {
            if (contentRef.current) {
              contentRef.current.removeEventListener('click', handleGlobalClick);
              console.log('Removed global click handler');
            }
          } catch (err) {
            console.error('Error cleaning up click handler:', err);
          }
        };
      }
    } else {
      console.log('Cannot render newsletter yet:', {
        hasContentRef: !!contentRef.current,
        hasNewsletterHtml: !!newsletterHtml,
        loading,
        error
      });
    }
  }, [newsletterHtml, loading, error, handleImagesLoaded, isSpecialPage]);

  // Function to handle returning to the regular newsletter view
  const handleReturnToRegular = useCallback(() => {
    console.log('Forcing navigation to https://e3d.ai/news');
    // Navigate directly to the e3d.ai news page by simulating a direct link click
    const link = document.createElement('a');
    link.href = 'https://e3d.ai/news';
    link.setAttribute('target', '_self'); // Force same window
    link.setAttribute('rel', 'noopener noreferrer');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);
  
  // Function to render special page content safely while preserving the E3D header
  const renderSpecialContent = () => {
    if (!specialPageContent) return null;
    
    try {
      // Create a temporary div to parse the HTML content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = specialPageContent;
      
      // Extract only the main content from the special page, excluding any headers
      // Look for the main content container with class newsletter-content
      const mainContent = tempDiv.querySelector('.newsletter-content') || 
                         tempDiv.querySelector('.main-content') ||
                         tempDiv.querySelector('.content-wrapper');
      
      // If we found the content container, only use that part
      // Otherwise, we'll use the entire content but exclude any header sections
      const contentHtml = mainContent ? mainContent.innerHTML : specialPageContent;

      console.log('Rendering special content with preserved E3D header');
      
      return (
        <div className="special-page-content" dangerouslySetInnerHTML={{ __html: contentHtml }} />
      );
    } catch (err) {
      console.error('Error rendering special content:', err);
      return <div>Error displaying content: {err.message}</div>;
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.contentWrapper}>
        <div className={classes.header}>
          <div className={classes.titleContainer}>
            <span onClick={() => window.goToHome()} style={{cursor: 'pointer'}}>
              <img src={futLogoIcon200} style={{width:'30px', height:'30px', paddingRight: 10}} alt="E3D Logo" />
            </span>
            <Typography variant="h5" className={classes.title}>
              E3D AI Newsletter
            </Typography>
          </div>
          {/* Download PDF button temporarily removed
          <Button
            className={classes.downloadButton}
            onClick={handleDownloadPDF}
            disabled={loading || error || pdfGenerating}
          >
            {pdfGenerating ? 'Generating...' : 
              <>
                <FaDownload className={classes.downloadIcon} /> 
                <span className={classes.downloadText}>Download PDF</span>
              </>
            }
          </Button>
          */}
        </div>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
            <Typography variant="body1" style={{ marginLeft: '10px' }}>
              Loading newsletter...
            </Typography>
          </div>
        ) : error ? (
          <div className={classes.error}>
            <Typography variant="body1">
              Error: {error}
            </Typography>
            {debugInfo.attempts.length > 0 && (
              <div className={classes.debug}>
                <Typography variant="caption">Debug info:</Typography>
                <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
              </div>
            )}
          </div>
        ) : (
          <Paper className={classes.content}>
            {isSpecialPage && (
              <div className="return-link-container" style={{ textAlign: 'right', marginBottom: '10px' }}>
                <Button 
                  variant="text" 
                  color="primary"
                  id="return-to-newsletter-btn"
                  className="return-to-newsletter-btn"
                  data-external-link="true"
                  startIcon={<span aria-hidden="true">←</span>}
                  onClick={(e) => {
                    // Stop event propagation to prevent other handlers from capturing it
                    e.stopPropagation();
                    e.preventDefault();
                    // Force navigation to /news on the CURRENT server (not hardcoded e3d.ai)
                    // Use window.location.origin to get the current protocol, domain, and port together
                    const currentOrigin = window.location.origin;
                    const newsUrl = `${currentOrigin}/news`;
                    console.log(`Navigating to news on current server: ${newsUrl}`);
                    // Use window.location.href for the most direct and reliable navigation
                    window.location.href = newsUrl;
                  }}
                >
                  Return to Newsletter
                </Button>
              </div>
            )}
            <div ref={contentRef}>
              {isSpecialPage ? (
                // If showing special page, use the renderSpecialContent function
                renderSpecialContent()
              ) : (
                // Otherwise the regular newsletter content will be injected via useEffect
                <div className="regular-newsletter-content" />
              )}
            </div>
          </Paper>
        )}
      </div>
    </div>
  );
};

// Wrap component with React.memo to prevent unnecessary re-renders
export default React.memo(NewsletterContent);
